import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import type { SupabaseClient, User } from '@supabase/supabase-js';

import type { CreateRubricRequest, Rubric, UpdateRubricRequest } from '@/shared/types/rubric';
import { sbq, throwOnError } from '@/shared/utils/supabaseQuery';
import { ensureUserRow } from '@/shared/utils/ensureUserRow';

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function ensureCurrentUserRow(sb: SupabaseClient, user: User): Promise<void> {
  await ensureUserRow(sb, user);
}

function mapRubric(row: any): Rubric {
  return {
    id: row.id ?? '',
    title: row.title ?? 'Untitled Rubric',
    description: row.description ?? null,
    ownerId: row.owner_id ?? '',
    createdAt: row.created_at ?? '',
    updatedAt: row.updated_at ?? '',
    isPublic: row.is_public ?? false,
    isDeleted: row.is_deleted ?? false,
    content: row.content ?? null,
  };
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const rubricsApi = createApi({
  reducerPath: 'rubricsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Rubric'],
  endpoints: (builder) => ({
    // ── Rubric CRUD ────────────────────────────────────────────────────────

    getRubricsByOwner: builder.query<Rubric[], string>({
      queryFn: sbq(async (sb, ownerId) => {
        const rows = throwOnError(
          await sb
            .from('rubrics')
            .select('*')
            .eq('owner_id', ownerId)
            .eq('is_deleted', false)
            .order('updated_at', { ascending: false })
        );
        return (rows ?? []).map(mapRubric);
      }, 'Rubric'),
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({ type: 'Rubric' as const, id: r.id })),
              { type: 'Rubric', id: 'LIST' },
            ]
          : [{ type: 'Rubric', id: 'LIST' }],
    }),

    getSharedRubrics: builder.query<Rubric[], string>({
      queryFn: sbq(async (sb, userId) => {
        // Get rubrics where user has a direct or team permission but is not owner
        const rows = throwOnError(
          await sb
            .from('rubrics')
            .select('*')
            .eq('is_deleted', false)
            .neq('owner_id', userId)
            .order('updated_at', { ascending: false })
        );
        // RLS handles filtering to only rubrics the user can see
        return (rows ?? []).map(mapRubric);
      }, 'Rubric'),
      providesTags: [{ type: 'Rubric', id: 'SHARED' }],
    }),

    getRubricById: builder.query<Rubric, string>({
      queryFn: sbq(async (sb, rubricId) => {
        const row = throwOnError(await sb.from('rubrics').select('*').eq('id', rubricId).single());
        return mapRubric(row);
      }, 'Rubric'),
      providesTags: (_result, _error, id) => [{ type: 'Rubric', id }],
    }),

    createRubric: builder.mutation<Rubric, CreateRubricRequest>({
      queryFn: sbq(async (sb, req) => {
        const { data, error } = await sb.auth.getUser();
        if (error) throw error;
        if (!data?.user) throw new Error('You must be logged in.');

        await ensureCurrentUserRow(sb, data.user);

        const row = throwOnError(
          await sb
            .from('rubrics')
            .insert({
              title: req.title,
              description: req.description ?? null,
              owner_id: data.user.id,
              is_public: req.isPublic ?? false,
              content: req.content ?? null,
            })
            .select('*')
            .single()
        );
        return mapRubric(row);
      }, 'Rubric'),
      invalidatesTags: [{ type: 'Rubric', id: 'LIST' }],
    }),

    updateRubric: builder.mutation<Rubric, UpdateRubricRequest>({
      queryFn: sbq(async (sb, { id, ...fields }) => {
        if (!id) throw new Error('Rubric ID required');
        const payload: Record<string, unknown> = {};
        if (fields.title !== undefined) payload.title = fields.title;
        if (fields.description !== undefined) payload.description = fields.description;
        if (fields.isPublic !== undefined) payload.is_public = fields.isPublic;
        if (fields.content !== undefined) payload.content = fields.content;

        const row = throwOnError(
          await sb.from('rubrics').update(payload).eq('id', id).select('*').single()
        );
        return mapRubric(row);
      }, 'Rubric'),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Rubric', id: args.id },
        { type: 'Rubric', id: 'LIST' },
      ],
      async onQueryStarted({ id, ...fields }, { dispatch, queryFulfilled }) {
        // Optimistic cache update — prevents flash when autosave triggers refetch
        const patchResult = dispatch(
          rubricsApi.util.updateQueryData('getRubricById', id, (draft) => {
            if (fields.title !== undefined) draft.title = fields.title;
            if (fields.description !== undefined) draft.description = fields.description ?? '';
            if (fields.isPublic !== undefined) draft.isPublic = fields.isPublic;
            if (fields.content !== undefined) draft.content = fields.content;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteRubric: builder.mutation<void, string>({
      queryFn: sbq(async (sb, id) => {
        throwOnError(
          await sb.from('rubrics').update({ is_deleted: true }).eq('id', id).select('id').single()
        );
        return undefined;
      }, 'Rubric'),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Rubric', id },
        { type: 'Rubric', id: 'LIST' },
        { type: 'Rubric', id: 'SHARED' },
      ],
    }),

    duplicateRubric: builder.mutation<Rubric, string>({
      queryFn: sbq(async (sb, id) => {
        const { data, error } = await sb.auth.getUser();
        if (error) throw error;
        if (!data?.user) throw new Error('You must be logged in.');

        await ensureCurrentUserRow(sb, data.user);

        const original: any = throwOnError(
          await sb.from('rubrics').select('*').eq('id', id).single()
        );

        const row = throwOnError(
          await sb
            .from('rubrics')
            .insert({
              title: `Copy of ${original.title}`,
              description: original.description,
              owner_id: data.user.id,
              is_public: false,
              content: original.content,
            })
            .select('*')
            .single()
        );
        return mapRubric(row);
      }, 'Rubric'),
      invalidatesTags: [{ type: 'Rubric', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetRubricsByOwnerQuery,
  useGetSharedRubricsQuery,
  useGetRubricByIdQuery,
  useCreateRubricMutation,
  useUpdateRubricMutation,
  useDeleteRubricMutation,
  useDuplicateRubricMutation,
} = rubricsApi;
